// DealsListPage — список сделок: фильтры (стадии/даты), сортировка, колонка «Компания»,
// колонка «След. контакт» + quick-action «Назначить следующий контакт» (R21.3),
// живой поиск q+fields (SearchFieldBar, V13 U-38 / канон LeadsListPage),
// пагинация + FAB "Создать сделку" (R17b.1), CSV-экспорт текущей выборки (R22, D18).
import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Fab,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DownloadIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { DealStageChip } from './components/DealStageChip';
import { AmountChip } from './components/AmountChip';
import { DealFiltersBar } from './components/DealFiltersBar';
import { NextContactDialog } from './components/NextContactDialog';
import {
  SearchFieldBar,
  type SearchFieldOption,
  type SearchFieldValue,
} from '@/components/SearchFieldBar';
import { dealsApi, type Deal, type DealDateField, type DealStage } from '@/api/deals';
import { useUserMap, resolveUserName } from '@/api/users';
import { useCompanyMap, resolveCompanyName } from '@/api/companies';
import { formatDate, formatDateOnly, formatStage } from '@/lib/format';
import {
  loadUserMapForExport,
  loadCompanyMapForExport,
  loadContactMapForExport,
  resolveNameOrDash,
} from '@/lib/exportResolve';
import { t } from '@/i18n';
import { useTableSort, type TableSortState } from '@/hooks/useTableSort';
import { usePageReset } from '@/hooks/usePageReset';
import { toCsv, downloadCsv, type CsvRow } from '@/lib/csv';

const DEFAULT_PAGE_SIZE = 20;

/** Размер страницы для CSV-экспорта: полная выборка одним запросом (KISS). */
const EXPORT_PAGE_SIZE = 1000;

/** Заголовки колонок CSV-экспорта сделок. */
const DEALS_CSV_HEADERS = [
  'Название',
  'Стадия',
  'Сумма',
  'Валюта',
  'Компания',
  'Контакт',
  'Владелец',
  'Дата создания',
  'Ожидаемая дата закрытия',
  'След. контакт',
  'Дата закрытия',
];

/** Порог «скорого» контакта: меньше суток до nextContactAt → warning-подсветка (мс). */
const NEXT_CONTACT_WARNING_THRESHOLD_MS = 24 * 60 * 60 * 1000;

/** Поля сортировки, доступные в UI (backend sort fields; nextContactAt — U-26). */
type DealSortField = 'createdAt' | 'amount' | 'nextContactAt';

/** Состояние сортировки: default createdAt,desc (backend default). */
const DEFAULT_SORT: TableSortState<DealSortField> = { field: 'createdAt', direction: 'desc' };

/**
 * Флажки полей поиска сделок (V13 U-38; whitelist DealSearchField, канон W1:
 * title/description/stage/currency/company_id/contact_id/owner_id — только эти apiName).
 */
function buildDealFieldOptions(): SearchFieldOption[] {
  return [
    { key: 'title', label: t('search.field.title') },
    { key: 'description', label: t('search.field.description') },
    { key: 'stage', label: t('search.field.stage') },
    { key: 'currency', label: t('search.field.currency') },
    { key: 'company_id', label: t('search.field.company_id') },
    { key: 'contact_id', label: t('search.field.contact_id') },
    { key: 'owner_id', label: t('search.field.owner_id') },
  ];
}

/** Рендерит ячейку «След. контакт»: «—» / дата / warning-chip при <24h. */
function NextContactCell({ deal }: { deal: Deal }): JSX.Element {
  if (!deal.nextContactAt) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid={`next-contact-${deal.id}`}>
        —
      </Typography>
    );
  }
  const diffMs = new Date(deal.nextContactAt).getTime() - Date.now();
  if (diffMs >= 0 && diffMs < NEXT_CONTACT_WARNING_THRESHOLD_MS) {
    return (
      <Chip
        size="small"
        color="warning"
        label={formatDate(deal.nextContactAt)}
        data-testid={`next-contact-${deal.id}`}
      />
    );
  }
  return (
    <Typography variant="body2" color="text.secondary" data-testid={`next-contact-${deal.id}`}>
      {formatDate(deal.nextContactAt)}
    </Typography>
  );
}

/** Страница списка сделок. */
export function DealsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  // V13 U-38: q + fields в одном состоянии (SearchFieldBar — единая панель, канон лидов/компаний).
  const [searchValue, setSearchValue] = useState<SearchFieldValue>({ q: '', fields: [] });
  const [stages, setStages] = useState<DealStage[]>([]);
  const [dueAfter, setDueAfter] = useState<string | null>(null);
  const [dueBefore, setDueBefore] = useState<string | null>(null);
  // U-27: поле дат-диапазона; дефолт — «Создана» (createdAt, решение Lead).
  const [dateField, setDateField] = useState<DealDateField>('createdAt');
  const { sort, setSort, handleSortClick } = useTableSort<DealSortField>(DEFAULT_SORT, setPage);
  const [nextContactDeal, setNextContactDeal] = useState<Deal | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const companyMap = useCompanyMap();
  // §6.2 №3: канон «фильтр → страница 0» через общий хук (причина бага FE-2 в задачах).
  const withPageReset = usePageReset(setPage);

  const query = useQuery({
    queryKey: ['deals', page, searchValue.q, searchValue.fields.join(','), stages, dueAfter, dueBefore, sort, dateField],
    queryFn: () =>
      dealsApi.list({
        page,
        size: DEFAULT_PAGE_SIZE,
        ...(stages.length > 0 ? { stages } : {}),
        ...(dueAfter ? { dueAfter } : {}),
        ...(dueBefore ? { dueBefore } : {}),
        ...(searchValue.q ? { q: searchValue.q } : {}),
        ...(searchValue.fields.length > 0 ? { fields: searchValue.fields.join(',') } : {}),
        sort: `${sort.field},${sort.direction}`,
        dateField,
      }),
  });

  // R23 / баг D21: резолвим только владельцев видимых строк (ids-режим /api/users?ids=).
  const ownerIds = useMemo(
    () =>
      (query.data?.content ?? [])
        .map((deal) => deal.ownerUserId)
        .filter((id): id is string => Boolean(id)),
    [query.data],
  );
  const userMap = useUserMap(ownerIds);

  /** Смена поиска (q после debounce / поля сразу) — сброс пагинации (канон LeadsListPage). */
  const handleSearchChange = withPageReset((next: SearchFieldValue): void => {
    setSearchValue(next);
  });

  /** Переключает стадию в multi-select фильтре и сбрасывает пагинацию. */
  const handleStageToggle = withPageReset((stage: DealStage): void => {
    setStages((prev) =>
      prev.includes(stage) ? prev.filter((s) => s !== stage) : [...prev, stage],
    );
  });

  /** Применяет диапазон дат (ISO Instant либо null) и сбрасывает пагинацию. */
  const handleDatesChange = withPageReset(
    (nextAfter: string | null, nextBefore: string | null): void => {
      setDueAfter(nextAfter);
      setDueBefore(nextBefore);
    },
  );

  /** Сброс всех фильтров и сортировки к дефолту. */
  const handleReset = withPageReset((): void => {
    setStages([]);
    setDueAfter(null);
    setDueBefore(null);
    setSort(DEFAULT_SORT);
    setDateField('createdAt');
  });

  /**
   * Экспорт CSV (R22): полная выборка (size=1000) с текущими фильтрами/сортировкой
   * и активным поиском q/fields (V13 U-38). Известное ограничение (M3, pre-existing R22):
   * backend кламппит size до 100 (Math.min(100, …) в DealWebMapper) → экспорт поиска =
   * максимум 100 найденных строк; вне V13.
   * FIX-C (U-15): имена владельцев/компаний/контактов резолвятся по ВСЕЙ экспортной
   * выборке (lookupByIds чанками + полные справочники), а не по page-scoped картам
   * видимой страницы (size=20) — иначе в CSV утекают короткие UUID.
   */
  const handleExportCsv = async (): Promise<void> => {
    setExporting(true);
    setExportError(null);
    try {
      const data = await dealsApi.list({
        page: 0,
        size: EXPORT_PAGE_SIZE,
        ...(stages.length > 0 ? { stages } : {}),
        ...(dueAfter ? { dueAfter } : {}),
        ...(dueBefore ? { dueBefore } : {}),
        ...(searchValue.q ? { q: searchValue.q } : {}),
        ...(searchValue.fields.length > 0 ? { fields: searchValue.fields.join(',') } : {}),
        sort: `${sort.field},${sort.direction}`,
        dateField,
      });
      const [exportUserMap, exportCompanyMap, exportContactMap] = await Promise.all([
        loadUserMapForExport(data.content.map((deal) => deal.ownerUserId)),
        loadCompanyMapForExport(data.content.map((deal) => deal.companyId)),
        loadContactMapForExport(data.content.map((deal) => deal.contactId)),
      ]);
      const rows: CsvRow[] = data.content.map((deal) => [
        deal.title,
        formatStage(deal.stage, t),
        deal.amount,
        deal.currency,
        resolveNameOrDash(exportCompanyMap, deal.companyId),
        resolveNameOrDash(exportContactMap, deal.contactId),
        resolveNameOrDash(exportUserMap, deal.ownerUserId),
        formatDate(deal.createdAt),
        formatDateOnly(deal.dueDate),
        formatDate(deal.nextContactAt),
        formatDate(deal.closedAt),
      ]);
      downloadCsv(`deals_${format(new Date(), 'yyyy-MM-dd')}.csv`, toCsv(rows, DEALS_CSV_HEADERS));
    } catch (error) {
      // FIX-C (LOW S4): без catch — unhandled rejection в async-обработчике клика.
      setExportError(error instanceof Error ? error.message : 'Неизвестная ошибка');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Сделки"
        subtitle="Управление сделками CRM"
        actions={
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
            disabled={exporting}
            aria-label="Экспорт CSV"
          >
            Экспорт CSV
          </Button>
        }
      />
      <Stack spacing={2} sx={{ mb: 2 }}>
        <SearchFieldBar
          fieldsOptions={buildDealFieldOptions()}
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={t('search.placeholder')}
          searchAriaLabel="Поиск сделок"
        />
        <DealFiltersBar
          stages={stages}
          onStageToggle={handleStageToggle}
          onDatesChange={handleDatesChange}
          dateField={dateField}
          onDateFieldChange={setDateField}
          onReset={handleReset}
        />
      </Stack>
      {exportError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Не удалось экспортировать CSV: {exportError}
        </Alert>
      ) : null}
      {query.isLoading ? (
        <LoadingSpinner label="Загрузка сделок…" />
      ) : query.isError ? (
        <EmptyState
          title="Не удалось загрузить сделки"
          description={query.error instanceof Error ? query.error.message : 'Неизвестная ошибка'}
        />
      ) : !query.data || query.data.content.length === 0 ? (
        <EmptyState
          title="Нет сделок"
          description="Создайте первую сделку"
          actionLabel="Создать сделку"
          onAction={() => navigate('/crm/deals/new')}
        />
      ) : (
        <Paper>
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Стадия</TableCell>
                  <TableCell sortDirection={sort.field === 'amount' ? sort.direction : false}>
                    <TableSortLabel
                      active={sort.field === 'amount'}
                      direction={sort.field === 'amount' ? sort.direction : 'asc'}
                      onClick={() => handleSortClick('amount')}
                    >
                      Сумма
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Компания</TableCell>
                  <TableCell>Владелец</TableCell>
                  <TableCell sortDirection={sort.field === 'createdAt' ? sort.direction : false}>
                    <TableSortLabel
                      active={sort.field === 'createdAt'}
                      direction={sort.field === 'createdAt' ? sort.direction : 'desc'}
                      onClick={() => handleSortClick('createdAt')}
                    >
                      Дата создания
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sort.field === 'nextContactAt' ? sort.direction : false}>
                    <TableSortLabel
                      active={sort.field === 'nextContactAt'}
                      direction={sort.field === 'nextContactAt' ? sort.direction : 'asc'}
                      onClick={() => handleSortClick('nextContactAt')}
                    >
                      След. контакт
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.content.map((deal: Deal) => (
                  <TableRow
                    key={deal.id}
                    hover
                    onClick={() => navigate(`/crm/deals/${deal.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {deal.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <DealStageChip stage={deal.stage} />
                    </TableCell>
                    <TableCell>
                      <AmountChip amount={deal.amount} currency={deal.currency} />
                    </TableCell>
                    <TableCell>
                      {deal.companyId ? (
                        <MuiLink
                          component={RouterLink}
                          to={`/crm/companies/${deal.companyId}`}
                          onClick={(e) => e.stopPropagation()}
                          variant="body2"
                        >
                          {resolveCompanyName(companyMap, deal.companyId)}
                        </MuiLink>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {resolveUserName(userMap, deal.ownerUserId)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(deal.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <NextContactCell deal={deal} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Назначить следующий контакт">
                        <IconButton
                          size="small"
                          aria-label="Назначить следующий контакт"
                          onClick={(e) => {
                            e.stopPropagation();
                            setNextContactDeal(deal);
                          }}
                        >
                          <EventAvailableIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={query.data.total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={DEFAULT_PAGE_SIZE}
            rowsPerPageOptions={[DEFAULT_PAGE_SIZE]}
            labelRowsPerPage={t('common.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common.of')} ${count}`}
          />
        </Paper>
      )}
      <Stack direction="row" justifyContent="flex-end">
        <Fab
          component={RouterLink}
          to="/crm/deals/new"
          color="primary"
          aria-label="Создать сделку"
          sx={{ position: 'fixed', bottom: 24, right: 24 }}
        >
          <AddIcon />
        </Fab>
      </Stack>
      {nextContactDeal ? (
        <NextContactDialog
          deal={nextContactDeal}
          open={Boolean(nextContactDeal)}
          onClose={() => setNextContactDeal(null)}
        />
      ) : null}
    </Box>
  );
}
